// Typed fetch wrapper. Centralized so we can add request-id propagation,
// retry logic, error normalization in one place.

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Dev/stub auth bootstrap.
//
// When SSO is disabled (dev), protected routes 401 until a session cookie
// exists. The backend's POST /auth/stub-login mints that cookie for the dev
// stub user. On the first 401 we establish that session once (deduped across
// concurrent requests) and retry. When SSO is enabled (production), the
// endpoint 404s — that's the signal to send the browser to the real SSO
// sign-in flow instead.
let stubSessionStatus: Promise<number> | null = null;

function ensureStubSession(): Promise<number> {
  if (!stubSessionStatus) {
    stubSessionStatus = fetch("/auth/stub-login", {
      method: "POST",
      credentials: "same-origin",
    })
      .then((r) => r.status)
      .catch(() => 0);
    // Don't cache a failure: a 404 (SSO on) or transient error shouldn't
    // permanently disable retries for the session.
    stubSessionStatus.then((status) => {
      if (status !== 200) stubSessionStatus = null;
    });
  }
  return stubSessionStatus;
}

// A 404 from /auth/stub-login means SSO is enabled and there's no dev stub to
// fall back to — the browser has no session and needs to go through the real
// sign-in flow. Guarded so concurrent 401s (feed, leaderboard, etc. all firing
// on mount) only trigger one navigation.
let redirectingToLogin = false;

function redirectToLogin(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  window.location.assign("/auth/login");
}

export async function apiClient<T>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  // FormData bodies (file uploads) must NOT get a JSON content-type — the
  // browser sets "multipart/form-data; boundary=..." itself when Content-Type
  // is left unset.
  const isFormData = init.body instanceof FormData;
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...init.headers,
    },
  });

  // First 401: try to establish the dev stub session, then retry once.
  if (response.status === 401 && !retried) {
    const stubStatus = await ensureStubSession();
    if (stubStatus === 200) {
      return apiClient<T>(path, init, true);
    }
    if (stubStatus === 404) {
      redirectToLogin();
    }
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // body is not JSON — that's fine
    }
    throw new ApiError(
      `${response.status} ${response.statusText}`,
      response.status,
      body,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
